import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Persistent sticky-top banner shown to OWNERS on a free trial.
 *
 * Hidden when:
 *   - user is null
 *   - user is not on a trial (accountStatus !== 'trial')
 *   - user already has an active subscription (they converted)
 *   - user is not an owner (teammates see the block screen, not a banner)
 *
 * Three tones based on `trialDaysRemaining`:
 *   > 3 days  → neutral teal "X days left in your trial"
 *   1–3 days  → amber "Only X days left"
 *   ≤ 0 days  → red "Your trial has ended" (defensive — backend gating
 *               redirects to /subscription first, but a stale page snapshot
 *               still reads correctly).
 */
const TrialBanner: React.FC = () => {
  const { user } = useAuth() as any;
  const navigate = useNavigate();

  if (!user) return null;
  if (user.role !== 'owner') return null;
  if (user.accountStatus !== 'trial') return null;
  if (user.subscription?.active) return null;

  const daysLeft: number | null =
    typeof user.trialDaysRemaining === 'number' ? user.trialDaysRemaining : null;

  let tone: 'neutral' | 'warning' | 'critical' = 'neutral';
  let text = "You're on a free trial.";
  if (daysLeft != null) {
    if (daysLeft <= 0) {
      tone = 'critical';
      text = 'Your trial has ended.';
    } else if (daysLeft <= 3) {
      tone = 'warning';
      text =
        daysLeft === 1
          ? 'Only 1 day left in your trial.'
          : `Only ${daysLeft} days left in your trial.`;
    } else {
      tone = 'neutral';
      text = `${daysLeft} days left in your trial.`;
    }
  }

  // Lanura palette tones (mirroring the .prepractice-rich-want amber +
  // teal patterns elsewhere in the codebase).
  const styles: Record<typeof tone, React.CSSProperties> = {
    neutral: {
      background: 'rgba(20, 155, 158, 0.10)',
      color: '#0f7a7d',
      borderBottomColor: 'rgba(20, 155, 158, 0.30)',
    },
    warning: {
      background: 'rgba(245, 158, 11, 0.12)',
      color: '#b45309',
      borderBottomColor: 'rgba(245, 158, 11, 0.40)',
    },
    critical: {
      background: 'rgba(239, 68, 68, 0.14)',
      color: '#b91c1c',
      borderBottomColor: 'rgba(239, 68, 68, 0.45)',
    },
  };

  return (
    <div
      role="status"
      className="trial-banner"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 60,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 12,
        padding: '8px 16px',
        borderBottom: '1px solid',
        fontSize: 13,
        fontWeight: 500,
        ...styles[tone],
      }}
    >
      <span>{text}</span>
      <button
        type="button"
        onClick={() => navigate('/subscription')}
        style={{
          background: '#0f172a',
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          padding: '4px 14px',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {tone === 'critical' ? 'Subscribe now →' : 'View plans'}
      </button>
    </div>
  );
};

export default TrialBanner;
